import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import { Iconify } from '../../iconify/Iconify';

type LargeBlockProps = React.ComponentProps<typeof LargeBlockRoot> & {
    title: string;
    tooltip?: string;
    canReset?: boolean;
    onReset?: () => void;
};

const LargeBlockRoot = styled('div')(({ theme }) => ({
    display: 'flex',
    position: 'relative',
    flexDirection: 'column',
    padding: theme.spacing(4, 2, 2, 2),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    borderRadius: theme.shape.borderRadius * 2,
    border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
}));

const LargeLabel = styled('span')(({ theme }) => ({
    top: -12,
    lineHeight: '22px',
    borderRadius: '22px',
    position: 'absolute',
    alignItems: 'center',
    display: 'inline-flex',
    padding: theme.spacing(0, 1.25),
    fontSize: theme.typography.pxToRem(13),
    color: theme.vars.palette.common.white,
    fontWeight: theme.typography.fontWeightSemiBold,
    backgroundColor: theme.vars.palette.text.primary,
    ...theme.applyStyles('dark', {
        color: theme.vars.palette.grey[800],
    }),
}));

/**
 * A component for displaying a large block with a title, optional tooltip, and reset functionality.
 *
 * @component
 * @param {object} props - The component props
 * @param {SxProps} [props.sx] - The style properties to apply to the root element
 * @param {React.ReactNode} props.title - The title to display for the block
 * @param {string} [props.tooltip] - Optional tooltip text to display when hovering over the info icon
 * @param {React.ReactNode} props.children - The content to display inside the block
 * @param {boolean} [props.canReset] - Whether to show the reset button
 * @param {() => void} [props.onReset] - Function to call when the reset button is clicked
 * @returns {React.ReactElement} The rendered LargeBlock component
 */
export function LargeBlock({
    sx,
    title,
    tooltip,
    children,
    canReset,
    onReset,
    ...other
}: LargeBlockProps) {
    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <LargeBlockRoot sx={sx} {...other}>
            <LargeLabel>
                {canReset && (
                    <ButtonBase disableRipple onClick={onReset} sx={{ ml: -0.5, mr: 0.5 }}>
                        <Iconify width={14} icon="solar:restart-bold" sx={{ opacity: 0.64 }} />
                    </ButtonBase>
                )}
                {title}
                {tooltip && (
                    <Tooltip title={tooltip} placement="right" arrow>
                        <Iconify
                            width={14}
                            icon="eva:info-outline"
                            sx={{ ml: 0.5, mr: -0.5, opacity: 0.48, cursor: 'pointer' }}
                        />
                    </Tooltip>
                )}
            </LargeLabel>

            {children}
        </LargeBlockRoot>
    );
}

type SmallBlockProps = React.ComponentProps<typeof SmallBlockRoot> & {
    label: string;
    canReset?: boolean;
    onReset?: () => void;
};

const SmallBlockRoot = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const SmallLabel = styled(ButtonBase, {
    shouldForwardProp: (prop: string) => !['canReset', 'sx'].includes(prop),
})<{ canReset?: boolean }>(({ theme }) => ({
    cursor: 'default',
    lineHeight: '16px',
    pointerEvent: 'none',
    alignSelf: 'flex-start',
    gap: theme.spacing(0.25),
    fontSize: theme.typography.pxToRem(11),
    color: theme.vars.palette.text.secondary,
    fontWeight: theme.typography.fontWeightSemiBold,
    transition: theme.transitions.create(['color']),
    variants: [
        {
            props: { canReset: true },
            style: {
                cursor: 'pointer',
                pointerEvent: 'auto',
                color: theme.vars.palette.text.primary,
                fontWeight: theme.typography.fontWeightBold,
                '&:hover': {
                    color: theme.vars.palette.primary.main,
                },
            },
        },
    ],
}));

/**
 * A small block component for settings drawer with optional reset functionality.
 *
 * @param {object} props - The component props
 * @param {React.ReactNode} props.label - The label text for the block
 * @param {boolean} [props.canReset] - Flag indicating if the block can be reset
 * @param {() => void} [props.onReset] - Callback function when reset button is clicked
 * @param {SxProps<Theme>} [props.sx] - The sx prop for custom styling
 * @param {React.ReactNode} props.children - The content to be rendered inside the block
 * @param {object} props.other - Other props to be spread to the root component
 *
 * @returns {JSX.Element} A small settings block with a label and optional reset functionality
 */
export function SmallBlock({ label, canReset, onReset, sx, children, ...other }: SmallBlockProps) {
    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <SmallBlockRoot sx={sx} {...other}>
            <SmallLabel disableRipple canReset={canReset} onClick={canReset ? onReset : undefined}>
                {canReset && <Iconify width={14} icon="solar:restart-bold" />}
                {label}
            </SmallLabel>
            {children}
        </SmallBlockRoot>
    );
}

export type OptionButtonProps = ButtonBaseProps & {
    selected?: boolean;
};

/**
 * A customized button component for option selection.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.selected - Whether the option is currently selected
 * @param {SxProps<Theme>} [props.sx] - The system props that allow defining system overrides as well as additional CSS styles
 * @param {React.ReactNode} props.children - The content of the button
 * @param {Object} props.other - Other props spread to the ButtonBase component
 *
 * @returns {JSX.Element} A styled ButtonBase component that visually represents an option
 *
 * @example
 * <OptionButton selected={isActive} onClick={handleClick}>
 *   <Icon /> Option Label
 * </OptionButton>
 */
export function OptionButton({ selected, sx, children, ...other }: OptionButtonProps) {
    return (
        <ButtonBase
            disableRipple
            sx={[
                (theme) => ({
                    width: 1,
                    borderRadius: 1.5,
                    lineHeight: '18px',
                    color: 'text.disabled',
                    border: `solid 1px transparent`,
                    fontWeight: 'fontWeightSemiBold',
                    fontSize: theme.typography.pxToRem(13),
                    ...(selected && {
                        color: 'text.primary',
                        bgcolor: 'background.paper',
                        borderColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                        boxShadow: `-8px 8px 20px -4px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
                        ...theme.applyStyles('dark', {
                            boxShadow: `-8px 8px 20px -4px ${varAlpha(theme.vars.palette.common.blackChannel, 0.12)}`,
                        }),
                        '& svg': {
                            color: 'primary.main',
                        },
                    }),
                }),
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...other}
        >
            {children}
        </ButtonBase>
    );
}
